// Live's AI edits of a layer (spec G13b, PR E2; live's `layer-edit-results.ts`): where a finished
// edit lands in the composition. "Replace layer" puts the edited picture in the layer's frame,
// its place, scale and name kept; "Add as new layer" puts it on top, fitted where the layer is,
// or centred when the layer is gone. Which edits exist and whether they finished is the server's
// list; where this browser asked each to land is only a hint, so a lost hint offers the result
// as a new layer instead.
import type { Job } from "../../../api/generated/model";
import { deliveredOutputs } from "../../../platform/jobs/output-picks";
import { isSettled } from "../../../platform/jobs/status";
import { committed, type History } from "./history";
import { added, containedFrame, fittedIn, placement, updated, visible, type Composition, type PictureLayer } from "./model";

/** Where an edit started here is to land. */
export interface EditLanding {
  /** The layer it edits. */
  layer: string;
  mode: "replace" | "add";
}

/** A finished edit's picture: its job's one output. */
export interface EditResult {
  jobId: string;
  outputIndex: number;
  url: string;
  width: number;
  height: number;
}

/** Live's words. */
export const EDIT_APPLIED = "Layer edit applied";
export const EDIT_FAILED = "The layer edit failed.";
export const EDIT_NOT_STARTED = "The layer edit could not start.";

/** The edits whose picture is in the composition already, removed since or not. */
export function appliedEdits(composition: Composition): Set<string> {
  return new Set(
    Object.values(composition.layers).flatMap((layer) =>
      layer.kind === "picture" && layer.source === "edit" && layer.ref.kind === "job_output" ? [layer.ref.job_id] : [],
    ),
  );
}

function edited(result: EditResult): Pick<PictureLayer, "source" | "ref" | "url" | "width" | "height"> {
  return {
    source: "edit",
    ref: { kind: "job_output", job_id: result.jobId, output_index: result.outputIndex },
    url: result.url,
    width: result.width,
    height: result.height,
  };
}

/**
 * The composition with a finished edit landed, and the layer it landed in (live selects a new
 * one). `landing` is where this browser asked it to land, or null for live's "Add as layer".
 */
export function landEdit(
  composition: Composition,
  result: EditResult,
  landing: EditLanding | null,
): { composition: Composition; layer: string; added: boolean } {
  const shown = new Set(visible(composition));
  const target = landing === null ? undefined : composition.layers[landing.layer];
  const there = target?.kind === "picture" && shown.has(target.id) ? target : undefined;
  if (landing?.mode === "replace" && there !== undefined) {
    const layer: PictureLayer = { ...there, ...edited(result), frame: fittedIn(there.frame, result.width, result.height) };
    return { composition: updated(composition, layer), layer: layer.id, added: false };
  }
  const over = target !== undefined && shown.has(target.id) ? target : undefined;
  const frame =
    over === undefined
      ? containedFrame(composition, result)
      : fittedIn(placement(composition, over.id), result.width, result.height);
  const layer: PictureLayer = {
    kind: "picture",
    id: `edit-${result.jobId}`,
    name: over === undefined ? "AI edit" : `${over.name} edit`,
    frame,
    ...edited(result),
  };
  return { composition: added(composition, layer), layer: layer.id, added: true };
}

/** A finished edit's picture, or null while it runs or when it failed. */
export function resultOf(job: Job): EditResult | null {
  if (job.status !== "succeeded") return null;
  const output = deliveredOutputs(job)[0];
  if (output === undefined || output.url === null || output.width === null || output.height === null) return null;
  return { jobId: job.id, outputIndex: output.index, url: output.url, width: output.width, height: output.height };
}

/** The Layerize's edits as the rail shows them: running ones, and finished ones neither in the
 * composition nor on their way to it (live's "Add as layer"). */
export function editsShown(edits: readonly Job[], composition: Composition, landings: Record<string, EditLanding>) {
  const applied = appliedEdits(composition);
  return {
    running: edits.filter((job) => !isSettled(job.status)),
    unapplied: edits.flatMap((job) => {
      const result = resultOf(job);
      return result === null || applied.has(job.id) || landings[job.id] !== undefined ? [] : [{ job, result }];
    }),
  };
}

/** The layers an edit started here is running on: their rows say so. */
export function busyLayers(edits: readonly Job[], landings: Record<string, EditLanding>): Set<string> {
  return new Set(edits.flatMap((job) => (!isSettled(job.status) && landings[job.id] !== undefined ? [landings[job.id]!.layer] : [])));
}

/**
 * The edits started here that have settled, landed as they asked: each finished one is one undo
 * step, once (an edit in the composition already is not landed again). `settled` are the edits
 * whose landing is done with; `failed` the first that failed, whose words the editor shows.
 */
export function settleEdits(history: History, edits: readonly Job[], landings: Record<string, EditLanding>) {
  let next = history;
  let selected: string | null = null;
  let landed = false;
  const settled = edits.filter((job) => landings[job.id] !== undefined && isSettled(job.status));
  for (const job of settled) {
    const result = resultOf(job);
    if (result === null || appliedEdits(next.present).has(job.id)) continue;
    const landing = landEdit(next.present, result, landings[job.id]!);
    next = committed(next, landing.composition);
    landed = true;
    if (landing.added) selected = landing.layer;
  }
  return {
    history: next,
    landed,
    selected,
    settled: settled.map((job) => job.id),
    failed: settled.find((job) => job.status !== "succeeded"),
  };
}

